import { instanceKey } from "@/lib/instanceKey";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { DataflowSummary } from "@shared/types/dataflows";
import type {
  CreateScheduleBody,
  ScheduleSummary,
  UpdateScheduleBody,
} from "@shared/types/schedules";
import { apiRequest } from "@/lib/api";
import { listQueryOptions } from "@/lib/listQuery";

/**
 * Schedule reads and writes (FR-50, FR-51).
 *
 * `queryKey[0]` is the literal request path, which is what the default query
 * function in `lib/queryClient.ts` fetches, so a list here and a list anywhere
 * else in the app share one cache entry rather than two that can disagree.
 *
 * Every mutation invalidates the list AND the single schedule it touched. The
 * single-schedule entry matters more than it looks: `next_fire_at` is computed
 * by the server on create, enable and any update that changes the firing time
 * (`ScheduleService`), so the value the editor was holding is stale the moment
 * a write returns, and a screen that kept showing it would be telling the user
 * the wrong time for the next run.
 */

export const SCHEDULES_PATH = "/api/v2/schedules";

/** Read from the connections API rather than through package (c)'s
 * `use-dataflows` hook: the schedule editor only ever needs the list, and going
 * through the same path means both surfaces share one cache entry without this
 * package owning a file another one does. */
export const DATAFLOWS_PATH = "/api/v2/dataflows";

export function schedulePath(id: string): string {
  return `${SCHEDULES_PATH}/${id}`;
}

/**
 * The key the schedule editor's detail query parks on while it is creating a
 * new schedule and has no id yet.
 *
 * A TanStack query with `enabled: false` still READS its cache key, so a detail
 * hook that fell back to the LIST's key would be handed the array of every
 * schedule and would type it as one row — which is exactly what happened: the
 * editor seeded its form from it and crashed reading `name` off an array. The
 * sentinel is a string nothing ever writes, so a disabled query resolves to
 * `undefined`, which is what "no id yet" means. It is deliberately not a path:
 * `${SCHEDULES_PATH}/` would also work today and reads like a real endpoint,
 * which is how it gets tidied back into a collision.
 *
 * `runScheduleHooks.keys.test.ts` asserts it collides with no real path.
 */
export const SCHEDULE_DISABLED_KEY = "schedule-detail-disabled";

/** `enabled: false` reads nothing (the run page while a notification link is blocked). */
export function useSchedules(enabled = true): UseQueryResult<ScheduleSummary[]> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<ScheduleSummary[]>({
    ...listQueryOptions,
    queryKey: instanceKey(SCHEDULES_PATH, selectedInstance?.id),
    enabled,
  });
}

/** One schedule, or nothing while the editor is creating a new one. */
export function useSchedule(id: string | null): UseQueryResult<ScheduleSummary> {
  return useQuery<ScheduleSummary>({
    queryKey: [id ? schedulePath(id) : SCHEDULE_DISABLED_KEY],
    enabled: Boolean(id),
  });
}

/**
 * Every connection in the instance, ready or not.
 *
 * The picker needs the not-ready ones too: FR-50 allows only a `ready`
 * connection on a schedule, and a picker that simply omits a draft connection
 * leaves the user hunting for something they can see on the Connections screen.
 * Offering it disabled, with its state named, answers the question instead.
 */
export function useInstanceConnections(enabled = true): UseQueryResult<DataflowSummary[]> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<DataflowSummary[]>({
    // The Connections list's own entry, so the same rule as `useDataflows`.
    ...listQueryOptions,
    queryKey: instanceKey(DATAFLOWS_PATH, selectedInstance?.id),
    enabled,
  });
}

/** Invalidate the list and, when named, one schedule. */
function useScheduleInvalidation(): (id?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (id?: string) => {
    await queryClient.invalidateQueries({ queryKey: [SCHEDULES_PATH] });
    if (id) await queryClient.invalidateQueries({ queryKey: [schedulePath(id)] });
  };
}

export function useCreateSchedule(): UseMutationResult<
  ScheduleSummary,
  unknown,
  CreateScheduleBody
> {
  const invalidate = useScheduleInvalidation();
  return useMutation({
    mutationFn: (body: CreateScheduleBody) =>
      apiRequest<ScheduleSummary>(SCHEDULES_PATH, { method: "POST", body }),
    onSuccess: (schedule) => invalidate(schedule.id),
  });
}

/**
 * The editor constructs this hook before it knows whether it is editing or
 * creating, because a hook cannot be called conditionally. The guard is what
 * stops an empty id becoming a `PUT /api/v2/schedules/` — a request to the list
 * route, which is not the same thing as doing nothing.
 */
function requireId(id: string, what: string): string {
  if (!id) throw new Error(`A ${what} needs a schedule id. This one was created without one.`);
  return id;
}

export function useUpdateSchedule(
  id: string,
): UseMutationResult<ScheduleSummary, unknown, UpdateScheduleBody> {
  const invalidate = useScheduleInvalidation();
  return useMutation({
    mutationFn: (body: UpdateScheduleBody) =>
      apiRequest<ScheduleSummary>(schedulePath(requireId(id, "schedule update")), {
        method: "PUT",
        body,
      }),
    onSuccess: () => invalidate(id),
  });
}

/**
 * FR-50's enabled flag. Enable and disable are separate routes rather than a
 * field on the update body, because enabling recomputes `next_fire_at` and
 * disabling must not: collapsing them into one PUT would make the editor's save
 * button quietly reschedule a run.
 */
export function useSetScheduleEnabled(
  id: string,
): UseMutationResult<ScheduleSummary, unknown, boolean> {
  const invalidate = useScheduleInvalidation();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      postEnabled(requireId(id, "schedule enable or disable"), enabled),
    onSuccess: () => invalidate(id),
  });
}

/** The same two routes, for a list where each row names its own schedule. */
export function useToggleSchedule(): UseMutationResult<
  ScheduleSummary,
  unknown,
  { id: string; enabled: boolean }
> {
  const invalidate = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({ id, enabled }) => postEnabled(id, enabled),
    onSuccess: (_saved, { id }) => invalidate(id),
  });
}

/** `POST /schedules/:id/enable` or `/disable` (audited as `schedule.toggle`). */
function postEnabled(id: string, enabled: boolean): Promise<ScheduleSummary> {
  return apiRequest<ScheduleSummary>(`${schedulePath(id)}/${enabled ? "enable" : "disable"}`, {
    method: "POST",
  });
}

export function useDeleteSchedule(): UseMutationResult<void, unknown, string> {
  const invalidate = useScheduleInvalidation();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(schedulePath(id), { method: "DELETE" }),
    onSuccess: (_result, id) => invalidate(id),
  });
}
